import { useState } from 'react';
import { Typography, notification } from 'antd';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useRoles from '../../hooks/useRoles';
import useSidebar from '../../hooks/useSidebar';
import { t } from '../../i18n/i18n';
import AccessInput from '../AccessInput/AccessInput';
import { isValidEmail } from '../../utils/emailsUtils';
import { Auth0Error, Platforms } from '../../constants/enums';
import { useLanguageTranslations } from '../../hooks/useLanguageTranslations';
import HorizontalLoader from '../HorizontalLoader/HorizontalLoader';
import PublicDashboardSecondStep from './PublicDashboardSecondStep';
import { handleAuthResult } from '../../auth0/customOTPLogin';
import useTermsAndConditions from '../../hooks/useTermsAndConditions';
import { colors } from '../../constants/colors';
import LogoutModal from '../LogoutModal/LogoutModal';
import './PublicDashboardFirstStep.scss';
import DebouncedButton from '../Button/Button';
import {
  acceptDashboardInvitation,
  dashboardInvitationSendCode,
} from '../../API/services/dashboardInvitation';
import { HOME } from '../../constants/routes';

const { Title, Text } = Typography;

const PublicDashboardFirstStep = ({ recruitSessionId }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { roles } = useRoles();
  const { setCollapsed } = useSidebar();
  const { setAcceptedTermsAndConditions } = useTermsAndConditions(Platforms.PARTNERS);
  const { languagesList } = useLanguageTranslations();
  const initialCodeOTP = Array.from({ length: 4 }, () => '');
  const [focus, setFocus] = useState(false);
  const [focusName, setFocusName] = useState(false);
  const [focusLastName, setFocusLastName] = useState(false);
  const [codeOTP, setCodeOTP] = useState(initialCodeOTP);
  const [codeSendSuccess, setcodeSendSuccess] = useState(false);
  const [email, setEmail] = useState('');

  const [isNewUser, setIsNewUser] = useState(false);
  const initialUserData = {
    name: '',
    lastName: '',
  };
  const [userData, setUserData] = useState(initialUserData);

  const [loading, setLoading] = useState(false);

  const handleSendCode = async () => {
    setLoading(true);
    try {
      const data = await dashboardInvitationSendCode({
        recruitSessionId,
        email,
        language: languagesList[0].value,
        platform: Platforms.PARTNERS,
        isSubmitWithoutAccount: true,
      });
      if (data) {
        notification.success({
          message: t('PUBLIC_DASHBOARD_OTP_EMAIL_SENT_SUCCESS_TITLE'),
          description: t('PUBLIC_DASHBOARD_OTP_EMAIL_SENT_SUCCESS_DESCRIPTION'),
          placement: 'topRight',
        });
        setcodeSendSuccess(true);
        setIsNewUser(data.isNewUser);
      }
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      const backendStatus = error?.response?.data?.statusCode;
      if (backendMessage.includes(Auth0Error.USER_NOT_OTP)) {
        notification.warning({
          message: t('OTP_EMAIL_SENT_FAILED_USER_EXIST_TITLE'),
          description: t('OTP_EMAIL_SENT_FAILED_USER_EXIST_DESCRIPTION'),
          placement: 'topRight',
          duration: 10,
        });
        history.push(HOME);
      } else if (backendStatus === 404) {
        notification.warning({
          message: t('OTP_EMAIL_SENT_FAILED_NOT_DASHBOARD_INVITATION'),
          description: t('OTP_EMAIL_SENT_FAILED_NOT_DASHBOARD_INVITATION_DESCRIPTION'),
          placement: 'topRight',
        });
      } else {
        notification.error({
          message: t('OTP_EMAIL_SENT_FAILED_TITLE'),
          description: t('OTP_EMAIL_SENT_FAILED_DESCRIPTION'),
          placement: 'topRight',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCode = () => {
    setEmail('');
    setUserData(initialUserData);
    setCodeOTP(initialCodeOTP);
    setcodeSendSuccess(false);
    setAcceptedTermsAndConditions(false);
  };

  const handleAcceptDashboardInvitation = async () => {
    const otp = codeOTP.join('');
    setLoading(true);
    try {
      const response = await acceptDashboardInvitation({
        email,
        platform: Platforms.PARTNERS,
        otp,
        ...(isNewUser && {
          firstName: userData.name,
          lastName: userData.lastName,
        }),
        recruitSessionId,
      });
      if (response.status === 201) {
        notification.success({
          message: t('OTP_EMAIL_SENT_SUCCESS_TITLE'),
          description: t('OTP_EMAIL_SENT_SUCCESS_DESCRIPTION'),
          placement: 'topRight',
        });
        await handleAuthResult({
          result: response.data,
          dispatch,
          roles,
          setCollapsed,
          isPublicDashboard: true,
        });

        setcodeSendSuccess(false);
        setLoading(false);
      }
    } catch (error) {
      notification.error({
        message: t('OTP_EMAIL_RECEIVED_FAILED_TITLE'),
        description: t('OTP_EMAIL_RECEIVED_FAILED_DESCRIPTION'),
        placement: 'topRight',
      });
      setLoading(false);
      handleCancelCode();
    }
  };

  const isDisabled = !isValidEmail(email);
  const backgroundColor = isDisabled ? colors.lightGrayAlkemy : colors.lightBlue;
  const hoverColor = isDisabled ? colors.lightGrayAlkemy : colors.blueAlkemy;

  return (
    <LogoutModal>
      <Title level={3} className="text-center fw-bold mb-2">
        {t('PUBLIC_DASHBOARD_TITLE')}
      </Title>
      <Text className="text-secondary text-center">{t('PUBLIC_DASHBOARD_DESCRIPTION')}</Text>
      {codeSendSuccess ? (
        <PublicDashboardSecondStep
          isNewUser={isNewUser}
          userData={userData}
          setUserData={setUserData}
          focusName={focusName}
          setFocusName={setFocusName}
          focusLastName={focusLastName}
          setFocusLastName={setFocusLastName}
          codeOTP={codeOTP}
          setCodeOTP={setCodeOTP}
          handleAcceptDashboardInvitation={handleAcceptDashboardInvitation}
          loading={loading}
        />
      ) : loading ? (
        <div className="d-flex justify-content-center my-4">
          <HorizontalLoader className="no-height" />
        </div>
      ) : (
        <>
          <AccessInput
            value={email}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            onChange={(e) => setEmail(e.target.value)}
            focus={focus}
            placeholder={t('PUBLIC_DASHBOARD_INPUT_PLACEHOLDER')}
          />
          <div className="d-flex flex-column flex-sm-row gap-4 w-100 justify-content-around">
            <DebouncedButton
              style={{
                backgroundColor,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
              }}
              className={`d-flex align-items-center justify-content-center text-white fw-semibold px-3 py-2 rounded custom-transition ${
                isDisabled ? '' : 'custom-hover-brightness'
              }`}
              disabled={isDisabled}
              onClick={handleSendCode}
              debounceTime={500}
              onMouseOver={(e) => {
                if (!isDisabled) e.currentTarget.style.backgroundColor = hoverColor;
              }}
              onFocus={(e) => {
                if (!isDisabled) e.currentTarget.style.backgroundColor = hoverColor;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = backgroundColor;
              }}
              onBlur={(e) => {
                e.currentTarget.style.backgroundColor = backgroundColor;
              }}
            >
              {t('PUBLIC_DASHBOARD_BUTTON')}
            </DebouncedButton>
          </div>
        </>
      )}
      {codeSendSuccess && (
        <Text
          style={{ color: colors.lightBlue, cursor: 'pointer' }}
          className="mt-20 custom-transition text-decoration-underline"
          onMouseOver={(e) => {
            e.currentTarget.style.color = colors.blueAlkemy;
          }}
          onFocus={(e) => {
            e.currentTarget.style.color = colors.blueAlkemy;
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.color = colors.lightBlue;
          }}
          onBlur={(e) => {
            e.currentTarget.style.color = colors.lightBlue;
          }}
          onClick={handleCancelCode}
        >
          {t('CANCEL')}
        </Text>
      )}
    </LogoutModal>
  );
};

export default PublicDashboardFirstStep;
